/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Component,
  ComponentInterface,
  Element,
  Host,
  Prop,
  State,
  h,
} from '@stencil/core';
import { createRipple, keyCodes } from '../../utils/utils';

@Component({
  tag: 'signet-tabs',
  styleUrl: 'signet-tabs.scss',
})
export class SignetTabs implements ComponentInterface {
  $selectedTab: HTMLElement = null;
  tabId = `tabs_${tabsIds++}`;

  @Element() $el: HTMLElement;

  /**
   * The position of the tabs. Can be top, bottom, left or right.
   */
  @Prop() position: 'top' | 'bottom' | 'left' | 'right' = 'top';
  /**
   * The alignment of the tabs. Can be start, center, end or justified.
   */
  @Prop() align: 'start' | 'center' | 'end' | 'justified' = 'start';
  /**
   * The variant of the tabs. Can be filled or outlined.
   */
  @Prop() size: 'small' | 'medium' | 'large' = 'medium';
  /**
   * The variant of the tabs. Can be filled or outlined.
   */
  @Prop() variant: 'filled' | 'outlined' = 'outlined';
  /**
   * The id of the tabs.
   */
  @Prop() label: string;

  @State() selectedIndex = 0;
  @State() $tabPanels: HTMLSignetTabElement[];
  @State() $tabs: HTMLButtonElement[];
  @State() $tabList: HTMLElement;

  connectedCallback() {
    this.initElements();
    this.initTabs();
  }

  componentDidLoad() {
    this.$tabs = Array.from(
      this.$el.querySelectorAll('[role="tab"]')
    ) as HTMLButtonElement[];
    const mo = new MutationObserver(() => {
      this.initElements();
      this.initTabs();
      this.$tabs = Array.from(
        this.$el.querySelectorAll('[role="tab"]')
      ) as HTMLButtonElement[];
    });
    mo.observe(this.$el, { childList: true });
  }

  initElements() {
    this.$tabPanels = Array.from(
      this.$el.querySelectorAll('signet-tab')
    ) as HTMLSignetTabElement[];
  }

  initTabs() {
    if (this.$tabPanels.length === 0) return;

    this.$tabPanels.forEach((tab, index) => {
      tab.hidden = index > 0;
      tab.id = tab.id || `${this.tabId}_tab_${index}`;
      tab.setAttribute(
        'aria-labelledby',
        `${tab.id || `${this.tabId}_tab_${index}`}_button`
      );
    });
  }

  clickHandler(e: MouseEvent, index: number) {
    const $tab = e.target as HTMLButtonElement;
    this.selectedIndex = index;

    const tabPanel = this.$tabPanels[index];
    const tabLink = tabPanel.getAttribute('link');
    const tabAction = tabPanel.getAttribute('action');

    if (tabLink) {
      window.location.href = tabLink;
      return;
    }

    if (tabAction) {
      new Function(tabAction)();
      return;
    }

    // Remove all current selected tabs
    this.$tabList
      .querySelectorAll('[aria-selected="true"]')
      .forEach((t) => t.setAttribute('aria-selected', 'false'));

    // Set this tab as selected
    $tab.setAttribute('aria-selected', 'true');

    // Hide all tab panels
    this.$tabPanels.forEach((p) => p.setAttribute('hidden', 'true'));

    // Show the selected panel
    this.$tabPanels[index].removeAttribute('hidden');
    createRipple(e.currentTarget as HTMLElement, e);
  }

  keydownHandler(e) {
    if (e.keyCode !== keyCodes.RIGHT_ARROW && e.keyCode !== keyCodes.LEFT_ARROW)
      return;

    this.deselectTab();

    e.keyCode === keyCodes.RIGHT_ARROW
      ? this.goToNextTab()
      : this.goToPreviousTab();

    this.selectTab();
  }

  goToNextTab() {
    this.selectedIndex++;
    // If it's the last tab, go to the first
    if (this.selectedIndex >= this.$tabs.length) {
      this.selectedIndex = 0;
    }
  }

  goToPreviousTab() {
    this.selectedIndex--;
    // If it's the first tab, move to the last
    if (this.selectedIndex < 0) {
      this.selectedIndex = this.$tabs.length - 1;
    }
  }

  deselectTab() {
    this.$tabs[this.selectedIndex].setAttribute('tabindex', '-1');
    this.$tabs[this.selectedIndex].setAttribute('aria-selected', 'false');
    this.$tabPanels[this.selectedIndex].setAttribute('hidden', 'true');
  }

  selectTab() {
    this.$tabs[this.selectedIndex].setAttribute('tabindex', '0');
    this.$tabs[this.selectedIndex].setAttribute('aria-selected', 'true');
    this.$tabPanels[this.selectedIndex].removeAttribute('hidden');
    this.$tabs[this.selectedIndex].focus();
  }

  render() {
    return (
      <Host class={`signet-tabs align-${this.position} ${this.variant}`}>
        <div
          role="tablist"
          class={`tab-list justify-${this.align}`}
          aria-label={this.label}
          ref={(el) => (this.$tabList = el)}
          onKeyDown={(e) => this.keydownHandler(e)}
        >
          {this.$tabPanels.map((tab, index) => (
            <button
              role="tab"
              class="tab-item"
              aria-selected={`${index === 0}`}
              aria-controls={tab.id || `${this.tabId}_tab_${index}`}
              id={`${tab.id || `${this.tabId}_${index}`}_button`}
              tabIndex={index === 0 ? 0 : -1}
              onClick={(e) => this.clickHandler(e as MouseEvent, index)}
              ref={(el) => (el.innerHTML = tab.tabText as string)}
            ></button>
          ))}
        </div>
        <slot />
      </Host>
    );
  }
}

let tabsIds = 0;
